import React, { useEffect, useState, useRef, useContext } from "react";
import {
  Typography,
  Button,
  CardContent,
  CardActions,
  Grid,
  Card,
  CardMedia,
  IconButton,
  TextField,
  Box,
} from "@mui/material";
import { getUser, getUsers, updateUser } from "../api";
import { Edit, Check, Close } from "@mui/icons-material";
import UserModal from "../components/UserModal";
import Loader from "../components/common/Loader";
import EmptyState from "../components/common/EmptyState";
import { PENDING_DJS, APPROVED_DJS } from "../constants";
import DjContext from "../contexts/DJContext";
import config from "../config";

const sortByCreatedAtDesc = (arr) => {
  return arr.slice().sort((a, b) => b.createdAt - a.createdAt);
};

export default function DJsPage() {
  const [state, setState] = useState({});

  const djContext = useContext(DjContext);

  const usernameOrEmailInputRef = useRef();

  const handleSearch = async () => {
    const searchTerm = usernameOrEmailInputRef.current.value.toLowerCase();

    if (!searchTerm) {
      const data = await getUsers("dj");
      const pendingDJs = data.filter((dj) => dj.status === "pending");
      const approvedDJs = data.filter((dj) => dj.status === "approved");

      setState((s) => ({
        ...s,
        data: [
          { label: "Pending DJs", data: sortByCreatedAtDesc(pendingDJs) },
          { label: "Active DJs", data: sortByCreatedAtDesc(approvedDJs) },
        ],
      }));
      return;
    }

    const searchedUser = await getUser(searchTerm, "dj");

    const pendingDJs = searchedUser.status === "pending" ? [searchedUser] : [];
    const approvedDJs =
      searchedUser.status === "approved" ? [searchedUser] : [];

    setState((s) => ({
      ...s,
      data: [
        { label: "Pending DJs", data: sortByCreatedAtDesc(pendingDJs) },
        { label: "Active DJs", data: sortByCreatedAtDesc(approvedDJs) },
      ],
    }));
  };

  const handleApprove = (item) => async () => {
    const updatedUser = await updateUser({ ...item, status: "approved" });

    setState((s) => {
      const pendingDJs =
        s.data.find((section) => section.label === "Pending DJs")?.data || [];
      const activeDJs =
        s.data.find((section) => section.label === "Active DJs")?.data || [];

      djContext.setPendingDjs(
        pendingDJs.filter((dj) => dj.id !== updatedUser.id).length
      );

      return {
        ...s,
        data: [
          {
            label: "Pending DJs",
            data: sortByCreatedAtDesc(
              pendingDJs.filter((dj) => dj.id !== updatedUser.id)
            ),
          },
          {
            label: "Active DJs",
            data: sortByCreatedAtDesc([...activeDJs, updatedUser]),
          },
        ],
      };
    });
  };

  const handleReject = (item) => async () => {
    const updatedUser = await updateUser({
      ...item,
      status: undefined,
      role: "user",
      name: item.name ?? item.username,
    });

    setState((s) => {
      const pendingDJs =
        s.data.find((section) => section.label === "Pending DJs")?.data || [];
      const activeDJs =
        s.data.find((section) => section.label === "Active DJs")?.data || [];

      djContext.setPendingDjs(
        pendingDJs.filter((dj) => dj.id !== updatedUser.id).length
      );

      return {
        ...s,
        data: [
          {
            label: "Pending DJs",
            data: sortByCreatedAtDesc(
              pendingDJs.filter((dj) => dj.id !== updatedUser.id)
            ),
          },
          {
            label: "Active DJs",
            data: sortByCreatedAtDesc([...activeDJs]),
          },
        ],
      };
    });
  };

  const handleEdit = (item) => () => {
    setState((s) => ({
      ...s,
      open: true,
      user: item,
      handleConfirm: async (body) => {
        const user = await updateUser(body);
        user.timestamp = Date.now();
        setState((prevState) => {
          const updatedData = prevState.data.map((section) => ({
            ...section,
            data: section.data.map((dj) => (dj.id === user.id ? user : dj)),
          }));
          return {
            ...prevState,
            data: updatedData,
          };
        });
      },
    }));
  };

  const handleClose = () => {
    setState((s) => ({
      ...s,
      open: false,
      user: undefined,
      handleConfirm: undefined,
    }));
  };

  useEffect(() => {
    (async () => {
      const user = JSON.parse(localStorage.getItem("user"));
      if (user) {
        let data = user.role === "admin" ? await getUsers("dj") : [];
        let pendingDJs = [];
        let approvedDJs = [];
        let finalData = [];

        if (data.length > 0) {
          pendingDJs = data.filter((dj) => dj.status === "pending");
          approvedDJs = data.filter((dj) => dj.status === "approved");

          finalData = [
            { label: "Pending DJs", data: sortByCreatedAtDesc(pendingDJs) },
            { label: "Active DJs", data: sortByCreatedAtDesc(approvedDJs) },
          ];
        }

        setState((s) => ({
          ...s,
          data: finalData,
          role: user.role,
        }));
      }
    })();
  }, []);

  return (
    <>
      <Grid
        container
        spacing={4}
        sx={{ maxWidth: "1600px", paddingTop: "80px", paddingX: 4 }}
        justifyContent={state.data?.length ? "left" : "center"}
      >
        {["admin"].includes(state.role) && (
          <Grid item xs={12}>
            <Card variant="outlined" sx={{ display: "flex", padding: 2 }}>
              <TextField
                autoComplete="off"
                margin="dense"
                id="name"
                label="Username or Email"
                type="text"
                variant="outlined"
                color="secondary"
                size="small"
                inputRef={usernameOrEmailInputRef}
                sx={{ flexGrow: 1, marginRight: 1 }}
                InputProps={{ style: { color: "#d8c8f4" } }}
                InputLabelProps={{ shrink: true }}
              />
              <Button
                type="submit"
                onClick={handleSearch}
                variant="outlined"
                color="secondary"
                size="small"
              >
                Search
              </Button>
            </Card>
          </Grid>
        )}
        {state.data ? (
          state.data.map((section) => (
            <Grid item xs={12} key={section.label}>
              <Typography
                variant="h6"
                sx={{ textAlign: "left", color: "white", marginBottom: "10px" }}
              >
                {section.label}
              </Typography>

              {section.data.length ? (
                <Grid container spacing={3}>
                  {section.data.map((item) => (
                    <Grid
                      item
                      xs={12}
                      sm={6}
                      md={4}
                      lg={3}
                      xl={2.4}
                      key={item.id}
                      sx={{ background: "transparent" }}
                    >
                      <Card
                        variant="outlined"
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          backgroundColor: "#4b445b",
                        }}
                      >
                        <CardMedia
                          component="img"
                          image={
                            item.timestamp
                              ? `${config.cdnUrl}/avatars/${item.id}?t=${item.timestamp}`
                              : `${config.cdnUrl}/avatars/${item.id}`
                          }
                          alt={item.name}
                          sx={{
                            width: 250,
                            height: 250,
                            margin: "5px auto 0px",
                            borderRadius: 1,
                          }}
                        />
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            flex: 1,
                            minWidth: "1px",
                            marginLeft: "10px",
                          }}
                        >
                          <CardContent sx={{ flex: 1 }}>
                            <Typography
                              gutterBottom
                              variant="h5"
                              component="div"
                              color="secondary"
                              noWrap
                            >
                              {item.name}
                            </Typography>
                            <Typography
                              variant="body2"
                              color="secondary"
                              mb={1}
                              noWrap
                            >
                              {item.username}
                            </Typography>
                            <Typography
                              variant="body2"
                              color="secondary"
                              mb={1}
                              noWrap
                            >
                              {item.email}
                            </Typography>
                            <Typography
                              variant="body2"
                              color="secondary"
                              mb={1}
                            >
                              {new Date(item.createdAt).toLocaleString([], {
                                day: "2-digit",
                                month: "2-digit",
                                year: "numeric",
                                hour: "2-digit",
                                minute: "2-digit",
                              })}
                            </Typography>
                          </CardContent>
                          <CardActions>
                            {["admin"].includes(state.role) &&
                              item.status !== "approved" && (
                                <IconButton
                                  aria-label="approve"
                                  onClick={handleApprove(item)}
                                  color="secondary"
                                  sx={{ marginLeft: "auto" }}
                                >
                                  <Check />
                                </IconButton>
                              )}
                            {["admin"].includes(state.role) &&
                              item.status !== "approved" && (
                                <IconButton
                                  aria-label="approve"
                                  onClick={handleReject(item)}
                                  color="secondary"
                                  sx={{ marginLeft: "auto" }}
                                >
                                  <Close />
                                </IconButton>
                              )}
                            {["admin"].includes(state.role) && (
                              <IconButton
                                aria-label="edit"
                                onClick={handleEdit(item)}
                                color="secondary"
                                sx={{ marginLeft: "auto" }}
                              >
                                <Edit />
                              </IconButton>
                            )}
                          </CardActions>
                        </Box>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              ) : (
                <EmptyState
                  emptyStateKey={
                    section.label === "Pending DJs" ? PENDING_DJS : APPROVED_DJS
                  }
                  emptyStateHeight="20vh"
                />
              )}
            </Grid>
          ))
        ) : (
          <Loader />
        )}
      </Grid>

      {state.open && (
        <UserModal
          venueId={state.venueId}
          user={state.user}
          handleConfirm={state.handleConfirm}
          handleClose={handleClose}
        />
      )}
    </>
  );
}
